import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useMonitoringData } from '../hooks/useMonitoringData';
import toast from '../utils/toast';

const MonitoringContext = createContext(null);

export const useMonitoring = () => {
  const context = useContext(MonitoringContext);
  if (!context) {
    throw new Error('useMonitoring must be used within MonitoringProvider');
  }
  return context;
};

export const MonitoringProvider = ({ children }) => {
  const {
    taskExecutions,
    systemHealth,
    systemAlerts,
    loading,
    isRefreshing,
    errors,
    lastUpdated,
    metrics,
    refreshAll,
    refreshDataType: refetchDataType,
  } = useMonitoringData();

  const loadData = useCallback(async () => {
    const result = await refreshAll();
    if (result.failed.length === 0) {
      toast.success('Monitoring data refreshed');
    } else if (result.failed.length < 3) {
      toast.warning(
        `${result.failed.length} monitoring source${result.failed.length === 1 ? '' : 's'} could not refresh`
      );
    } else {
      toast.error('Monitoring data could not be refreshed');
    }
    return result;
  }, [refreshAll]);

  const refreshDataType = useCallback(async (dataType) => {
    const result = await refetchDataType(dataType);
    const label = dataType.charAt(0).toUpperCase() + dataType.slice(1);
    if (result.isError) {
      toast.error(`${label} data could not be refreshed`);
    } else {
      toast.success(`${label} data refreshed`);
    }
    return result;
  }, [refetchDataType]);

  const value = useMemo(() => ({
    taskExecutions,
    systemHealth,
    systemAlerts,
    loading,
    isRefreshing,
    errors,
    lastUpdated,
    loadData,
    refreshDataType,
    metrics,
  }), [
    errors,
    isRefreshing,
    lastUpdated,
    loadData,
    loading,
    metrics,
    refreshDataType,
    systemAlerts,
    systemHealth,
    taskExecutions,
  ]);

  return (
    <MonitoringContext.Provider value={value}>
      {children}
    </MonitoringContext.Provider>
  );
};

